import { Arr, assert, Callback1, Destructor, NullFunction, Store, VoidCallback, VoidCallback1 } from '@deqo/foundation';
import { EventSink } from '@deqo/podopolis';
import { formatDistance } from 'date-fns';
import Fuse from 'fuse.js';
import * as polished from 'polished';
import React, { useCallback, useEffect, useState } from 'react';
import { alea } from 'seedrandom';
import sha256 from 'tiny-sha256';
import { v4 as uuidV4 } from 'uuid';
import { AppEvents, AppEventStreams } from './app/AppEvents';
import { useResolve } from './app/Container';
import { LegacySVGProps, LFC } from './Legacy';
import { Colors, remInPx } from './ui';

export type primitive = boolean | number | string;

export const invokeLater: { (func: VoidCallback): Destructor, (afterMs: number, func: VoidCallback): Destructor } =
    (...args: any[]) => {
        const id = args.length === 1
            ? setTimeout(() => args[0](), 0)
            : setTimeout(() => args[1](), args[0]);
        return () => clearTimeout(id);
    };

export const invokeOnNextFrame = (func: VoidCallback) => {
    requestAnimationFrame(func);
};

export const requestAnimationFrameAfter = (afterMs: number, func: VoidCallback) => {
    const ta = performance.now();
    const tick = () => {
        const tb = performance.now();
        if (tb - ta >= afterMs) {
            func();
        }
        else {
            requestAnimationFrame(tick);
        }
    };
    requestAnimationFrame(tick);
};

export const createFrameWindowFunction = () => {
    let primed = false;
    return (func: VoidCallback) => {
        if (primed) return;
        primed = true;
        invokeOnNextFrame(() => {
            func();
            primed = false;
        });
    };
};

export function assertNot(expr: boolean, message: string): void {
    assert(!expr, message);
}

export function assertVal(value: any, name: string): asserts value {
    assert(value !== null && value !== undefined, `${name} is null or undefined.`);
}

export function assertStr(value: any, name: string): asserts value is string {
    assertVal(value, name);
    assert(typeof (value) === 'string', `${name} is not a string.`);
}

export function assertNum(value: any, name: string): asserts value is number {
    assertVal(value, name);
    assert(typeof (value) === 'number', `${name} is not a number.`);
}

export const uuid = (style?: 'default' | 'nodash') => {
    const val = uuidV4();
    return (style === 'nodash') ? val.replace(/\-/g, '') : val;
};

export const assignSafe = (from: any, to: any) => {
    for (const name of Object.getOwnPropertyNames(from)) {
        if (name in to && to[name] !== undefined) {
            from[name] = to[name];
        }
    }
};

// export type PickPropsNames<T> = { [K in keyof T]: T[K] extends Function ? never : K; }[keyof T];
// export type PickProps<T> = { [P in PickPropsNames<T>]: T[P] };

export type ObserverProps<T> = { store: Store<T>, render: Callback1<React.ReactNode | void, T> };

export class Observer<T> extends React.Component<ObserverProps<T>> {

    private subscription: Destructor;

    public state = {
        value: this.props.store.getState()
    };

    public render() {
        return this.props.render(this.state.value) || null;
    }

    public componentDidMount(): void {
        this.subscription = this.props.store.observe(value => this.setState({ value }));
    }

    public componentWillUnmount(): void {
        this.subscription();
    }
}

export const useObserver: <T>(store: Store<T>) => T = (store) => {
    const value = useCallback(() => !!store ? store.getState() : null, [store]);
    const [state, setState] = useState(value());
    useEffect(() => {
        setState(value());
        if (store) {
            return store.observe(setState);
        }
    }, [store]);
    return state;
};

export const useObserverReturningStore: <T, S extends Store<T>>(store: S) => S = (store) => {
    const value = useCallback(() => !!store ? store.getState() : null, [store]);
    const [_, setState] = useState(value());
    useEffect(() => {
        setState(value());
        if (store) {
            return store.observe(setState);
        }
    }, [store]);
    return store;
};

export const useDraft = <T extends Record<string, unknown>>(initial: T) => {
    const [draft, setDraft] = useState(initial);
    const patchDraft = (patch: Partial<T>) => setDraft(x => ({ ...x, ...patch }));
    const resetDraft = useCallback(() => setDraft(initial), [initial]);
    const isDraftDirty = !!Object.keys(draft).find(x => draft[x] !== initial[x]);
    return [draft, patchDraft, resetDraft, isDraftDirty, setDraft] as const;
};

export const useDraft2 = <T extends Record<any, any>>(initial: T) => {
    const [base, setBase] = useState(initial);
    const [draft, setDraft] = useState(initial);
    const patchDraft = (patch: Partial<T>) => setDraft(x => ({ ...x, ...patch }));
    const resetDraft = useCallback((to?: T) => {
        setBase(to || base);
        setDraft(to || base);
    }, [base]);
    const draftDirty = !!Object.keys(draft).find(x => draft[x] !== base[x]);
    const clearDraftDirty = useCallback(() => setBase(draft), [draft]);

    return { draft, draftDirty, patchDraft, setDraft, resetDraft, clearDraftDirty };
};

export const CloseButton: LFC<LegacySVGProps & { size?: string, color?: string, withBorder?: boolean }> = (props) => {
    const { size, color, withBorder, style, ...otherProps } = props;
    const fromProps = {} as any;
    if (color) fromProps.color = color;
    const defaults = {
        cursor: 'pointer',
        width: remInPx(size || 1.5),
        height: remInPx(size || 1.5),
    };
    return <svg style={Object.assign(defaults, style, fromProps)} {...otherProps} viewBox="0 0 24 24" fill="none" >
        {!withBorder && <>
            <path d="M6 18L18 6M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </>}
        {withBorder && <>
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
            <path d="M8 16L16 8M8 8L16 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </>}
    </svg>;
};


export type Metadata = Readonly<Record<string, any>>;

export const listen = (t: EventTarget, e: string, l: EventListenerOrEventListenerObject, opts?: AddEventListenerOptions): Destructor => {
    t.addEventListener(e, l, opts);
    return () => t.removeEventListener(e, l, opts);
};

// Based on nm8
// https://raw.githubusercontent.com/davidkpiano/nm8/master/src/nm8.ts

export interface Animation {
    play: () => Promise<boolean>;
    stop: () => void;
}

export type AnimationEasing = (t: number) => number;

export function animate(fn: (deltaOrOffset: number) => any, duration?: number): Animation {
    let rate: number;
    let currentTime: number;
    let elapsed: number;
    let done: VoidCallback1<boolean>;

    const tick = (timeStamp: number) => {
        const delta = +!rate || -(currentTime || timeStamp) + (currentTime = timeStamp);
        fn(duration ? Math.min(Math.max((elapsed += delta) / duration, 0), 1) : delta);
        if (!rate) done(false);
        else if (elapsed >= duration) done(true);
        else requestAnimationFrame(tick);
    };
    const anim = {
        play: () => {
            rate = 1;
            if (!(duration && elapsed <= duration)) {
                elapsed = 0;
            }
            tick(performance.now());
            return new Promise<boolean>((rs) => done = rs);
        },
        stop: () => {
            if (!!rate) {
                elapsed = currentTime = rate = 0;
                done(false);
            }
        }
    };
    return anim;
}

export const hash = (type: 'sha256', data: string): string => {
    return sha256(data);
};

export type MemoHasher<T> = (x: T) => string | (string | number | boolean)[];
export type MemoProvider<I, V> = (x: I) => V;

export class MemoCache {

    private entries = {} as Record<string, { hash?: string, value?: any, hasher: any }>;

    public create<V, I>(id: string, hasher: MemoHasher<I>, provider: MemoProvider<I, V>) {
        this.register(id, hasher);
        return (x: I) => {
            if (this.compare(id, x)) {
                return this.get(id);
            }
            else {
                const value = provider(x);
                this.set(id, x, value);
                return value;
            }
        };
    }

    public register<I>(id: string, hasher: MemoHasher<I>) {
        this.entries[id] = { hasher: this.normalizeHasher(hasher) };
    }

    public get<V = any>(id: string): V {
        return this.entries[id].value;
    }

    public set<I, V>(id: string, input: I, value: V): void {
        const entry = this.entries[id];
        entry.hash = entry.hasher(input);
        entry.value = value;
    }

    public compare<I>(id: string, input: I): boolean {
        const entry = this.entries[id];
        if ('hash' in entry) {
            return entry.hash === entry.hasher(input);
        }
        return false;
    }

    private normalizeHasher<I>(hasher: MemoHasher<I>): MemoHasher<I> {
        return (x: I) => {
            const r = hasher(x);
            return Array.isArray(r) ? JSON.stringify(r) : r;
        };
    }
}

export const waitFor = (ms: number) => new Promise<void>(rs => setTimeout(rs, ms));
export const waitForNextFrame = () => new Promise<void>(rs => requestAnimationFrame(() => rs()));

/**
 * Extracts map, reduce, filter and find from the Array interface.
 */

export interface MapReduceFilterFind<T> {

    map<U>(callbackfn: (value: T, index: number, array: T[]) => U, thisArg?: any): U[];

    reduce(callbackfn: (previousValue: T, currentValue: T, currentIndex: number, array: T[]) => T): T;
    reduce(callbackfn: (previousValue: T, currentValue: T, currentIndex: number, array: T[]) => T, initialValue: T): T;

    reduce<U>(callbackfn: (previousValue: U, currentValue: T, currentIndex: number, array: T[]) => U, initialValue: U): U;

    filter<S extends T>(predicate: (value: T, index: number, array: T[]) => value is S, thisArg?: any): S[];

    filter(predicate: (value: T, index: number, array: T[]) => unknown, thisArg?: any): T[];

    find<S extends T>(predicate: (this: void, value: T, index: number, obj: T[]) => value is S, thisArg?: any): S | undefined;
    find(predicate: (value: T, index: number, obj: T[]) => unknown, thisArg?: any): T | undefined;
}

export function once<F extends (...args: any[]) => void>(func: F): (this: ThisParameterType<F>, ...args: Parameters<F>) => void {
    let hasBeenCalled = false;
    return function (this: ThisParameterType<F>, ...args: Parameters<F>) {
        if (hasBeenCalled) return;
        func.apply(this, args);
        hasBeenCalled = true;
    };
}

export const debounce = <F extends (...args: any[]) => void>(func: F, waitMs = 0, isImmediate?: boolean) => {
    let timeoutId: any = -1;

    return function (this: ThisParameterType<F>, ...args: Parameters<F>) {

        const doLater = function () {
            timeoutId = -1;
            if (!isImmediate) {
                func.apply(this, args);
            }
        };

        const shouldCallNow = isImmediate && timeoutId === -1;

        if (timeoutId !== -1) {
            clearTimeout(timeoutId);
        }

        timeoutId = setTimeout(doLater, waitMs);

        if (shouldCallNow) {
            func.apply(this, args);
        }
    };
};

export const derecurse = <F extends (...args: any[]) => void>(func: F) => {
    let depth = 0;
    return function (this: ThisParameterType<F>, ...args: Parameters<F>) {
        depth++;
        if (depth === 1) {
            func.apply(this, args);
        }
        depth--;
    };
};

export const getter = <T extends unknown>(value: T) => () => value;

export type IdGenerator = () => string;
export const idGenerator = (prefix?: string) => {
    const state = { n: 0 };
    return () => `${prefix ?? ''}${++state.n}`;
};

export const promptName = idGenerator('pn');

// export interface PipeProps<S, F extends keyof S> {
//     source: EventSource<S>;
//     filter: F;
//     to: EventSink<S[F], F>;
// }

// export function Pipe<S, F extends keyof S>({ source, filter, to }: PipeProps<S, F>): React.ReactElement | null {
//     useEffect(() => source.on(filter, to), [source, filter, to]);
//     return null;
// }

// export const colors = {
//     primary10: polished.rgba(Colors.primary, .1),
//     primary20: polished.rgba(Colors.primary, .2),
//     primary30: polished.rgba(Colors.primary, .3),
//     primary40: polished.rgba(Colors.primary, .4),
//     primary50: polished.rgba(Colors.primary, .5),
//     primary60: polished.rgba(Colors.primary, .6),
//     primary70: polished.rgba(Colors.primary, .7),
//     primary80: polished.rgba(Colors.primary, .8),
//     primary90: polished.rgba(Colors.primary, .9),
//     primaryText10: polished.rgba(Colors.primaryText, .1),
//     primaryText20: polished.rgba(Colors.primaryText, .2),
//     primaryText30: polished.rgba(Colors.primaryText, .3),
//     primaryText40: polished.rgba(Colors.primaryText, .4),
//     primaryText50: polished.rgba(Colors.primaryText, .5),
//     primaryText60: polished.rgba(Colors.primaryText, .6),
//     primaryText70: polished.rgba(Colors.primaryText, .7),
//     primaryText80: polished.rgba(Colors.primaryText, .8),
//     primaryText90: polished.rgba(Colors.primaryText, .9),
// };

export const cieq = (a?: string, b?: string) => {
    return typeof a === 'string' && typeof b === 'string'
        ? a.localeCompare(b, undefined, { sensitivity: 'accent' }) === 0
        : a === b;
};

export const pxInRem = (r?: number | string | null): number => typeof r !== 'number'
    ? pxInRem(parseFloat(r as any))
    : r / (window.devicePixelRatio > 1 ? 12 : 16);

export const pxToRem = (r?: number | string | null): string => `${pxInRem(r)}rem`;

export const obfuscateStr = (str: string) => {
    const az19 = 'abcdefghijklmnopqrstuvwxyz123456789';
    return str.replace(/[^\x00-\x7F]/g, () => az19[Math.floor(Math.random() * az19.length)] ?? az19[0]);
};

export const hexToRgb = (hex) => {
    // https://stackoverflow.com/questions/21646738/convert-hex-to-rgba
    let c;
    if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
        c = hex.substring(1).split('');
        if (c.length === 3) {
            c = [c[0], c[0], c[1], c[1], c[2], c[2]];
        }
        c = '0x' + c.join('');
        return { r: (c >> 16) & 255, g: (c >> 8) & 255, b: c & 255 };
    }
    return null;
};

export const unixTime = () => Math.floor((new Date()).getTime() / 1000);

export const toInitials = (name?: string, all?: boolean) => {
    if (!name || !name.length) return '';
    name = name.replace(/[-'`~!@#$%^&*()_|+=?;:'",.<>\{\}\[\]\\\/]/gi, '');
    if (name.length === 2) return name;
    const iz = name.toUpperCase().split(' ').map(x => x.trim()[0]).filter(x => !!x);
    if (!all && iz.length > 1) {
        return `${iz[0]}${Arr.last(iz)}`;
    }
    return iz.join('');
};

const instanceStore = {} as any;
export const useOnceForSessionValue = <T extends unknown>(key: string, value: T): T => {
    if (!(key in instanceStore)) {
        instanceStore[key] = value;
    }
    return instanceStore[key] as T;
};

export const format = Object.freeze({
    date: (date: Date) => (date && date instanceof Date) ? `${date.toLocaleDateString()}` : '',
    time: (date: Date) => (date && date instanceof Date) ? `${date.toLocaleTimeString()}` : '',
    datetime: (date: Date) => (date && date instanceof Date) ? `${date.toLocaleDateString()} @ ${date.toLocaleTimeString()}` : '',
    since: (date: Date) => (date && date instanceof Date) ? formatDistance(date, new Date(), { addSuffix: true }) : '',
});

export const tryAnd = function <R, F extends (...args: any[]) => R>(func: F, error?: (err: Error) => void) {
    try {
        return func();
    }
    catch (e) {
        (error || NullFunction)(e);
        return undefined;
    }
};

export const srand = (seed: string) => alea(seed, { global: true })();

export const clamp = (num: number, to: number) => Math.round(num / to) * to;

export const boxInt = (num: number, min: number, max: number) => Math.min(max, Math.max(min, num));
export const boxIntRange = (num: number, range: number[]) => {
    if (num <= range[0] || range.length === 1) return range[0];
    if (num >= range[range.length - 1]) return range[range.length - 1];
    const i = range.sort((a, b) => a - b).findIndex(x => num < x);
    const l = range[i - 1];
    const u = range[i];
    const diff = u - l;
    return (Math.round((num - l) / diff) * diff) + l;
};

export const dataUrlToBlob = (url: string, type = 'image/png') => {
    // This is one of a few shite techniques to turn a data URL into a blob...
    const bin = atob(url.split(',')[1]);
    return new Blob(
        [new Uint8Array(Array.from(bin).map(x => x.charCodeAt(0)))],
        { type }
    );
};

export const shadeSafe = (percentage: string | number, color: string) =>
    polished.shade(percentage, Colors.normalize(color) || Colors.primary);
export const tintSafe = (percentage: string | number, color: string) =>
    polished.tint(percentage, Colors.normalize(color) || Colors.primary);
export const transparentizeSafe = (percentage: string | number, color: string) =>
    polished.transparentize(percentage, Colors.normalize(color) || Colors.primary);

export const toDataProps = (obj: Record<string, unknown>) =>
    Object.fromEntries(Object.entries(obj).map(([k, v]) => [`data-${k}`, v.toString()]));

export const toPromise = <T extends unknown>(input: T | Promise<T>): Promise<T> => {
    const i = input as any;
    return (typeof (i) === 'object' && 'then' in i && 'catch' in i)
        ? i as Promise<T>
        : Promise.resolve(i) as Promise<T>;
};

export const profileBlock = function <R, F extends (...args: any[]) => R>(name: string, func: F) {
    const t = performance.now();
    try {
        return func();
    }
    finally {
        console.log(name, (performance.now() - t) + 'ms');
    }
};

export const useSvgId = () => {
    const [id] = useState(() => uuid());
    return {
        value: id,
        url: `url(#${id})`
    };
};

export const useAppEvent = <K extends keyof AppEventStreams>(type: K, sink: EventSink<AppEventStreams[K], K>) => {
    const events = useResolve(AppEvents);
    useEffect(() => events.on(type, sink), [type, sink]);
};

export const removeUndefined = <T extends Record<string, any>>(obj: T) => {
    const copy = { ...obj };
    for (const key in copy) {
        if (copy[key] === undefined) {
            delete copy[key];
        }
    }
    return copy;
};

export type FuzzySearchParams<T> = {
    items: T[],
    keys: Fuse.FuseOptionKey[],
    search: string,
    threshold?: number,
};

export const fuzzySearch = <T extends unknown>({ items, keys, search, threshold = 0.1 }: FuzzySearchParams<T>) => {
    if (!search) return items;
    if (!items?.length) return items;
    const fuse = new Fuse(items, { includeScore: true, ignoreLocation: true, keys });
    const results = fuse
        .search(search)
        .filter(x => x.score < threshold)
        .sort((a, b) => a.score - b.score)
        .map(x => x.item);
    return results ?? [];
};

export const toSearchable = (input: string): string => {
    const cleaned = input
        .replace(/[^a-zA-Z0-9\s]/g, '')
        .replace(/\s\s+/g, ' ');
    return cleaned;
};

export const dailyRandom = <T extends any>(arr: T[]) => {
    const day = new Date().getDate();
    return arr[day % arr.length];
};

export const toClassNames = (...strs: (string | undefined)[]) =>
    strs
        .filter(x => !!x)
        .map(x => x!.replace(/[^\-a-zA-Z0-9\s]/g, '').replace(/\s+/g, '-'))
        .join(' ');

export const classNames = (...strs: (string | undefined)[]) => ({ className: toClassNames(...strs) });

export const deleteSearchParam = (key: string) => {

    const params = new URLSearchParams(window.location.search);
    const value = params.get(key);

    // Remove params, action and camera from URL, but leave other parameters
    if (value !== undefined) {
        params.delete(key);
        const count = Array.from(params.keys()).length;
        const query = !!count ? `?${params.toString()}` : '';
        window.history.replaceState({}, '', `${window.location.pathname}${query}`);
    }

    return value;
};

export const toPathNoDoubleSlash = (elmts: any[]) => elmts.join('/').replace(/\/\//g, '/');

/**
 * Recursively cleans a JSON object by removing null, undefined, empty strings, and resulting empty objects/arrays
 * @param {Object|Array} obj - Input to clean
 * @returns {Object|Array} - Cleaned result
 */
export const deepCleanEmptyOrNull = (obj) => {
    // Handle null/undefined input
    if (obj == null) return null;

    // Handle arrays
    if (Array.isArray(obj)) {
        return obj
            .filter(item => item != null && item !== '')
            .map(item => typeof item === 'object' ? deepCleanEmptyOrNull(item) : item)
            .filter(item => !(Array.isArray(item) && item.length === 0) &&
                !(typeof item === 'object' && item !== null && Object.keys(item).length === 0));
    }

    // Handle objects
    if (typeof obj === 'object') {
        const result = {};

        Object.entries(obj).forEach(([key, value]) => {
            // Skip null, undefined, empty strings
            if (value == null || value === '') return;

            // Handle nested objects/arrays
            if (typeof value === 'object') {
                const cleaned = deepCleanEmptyOrNull(value);

                // Skip if cleaned result is empty
                if ((Array.isArray(cleaned) && cleaned.length === 0) ||
                    (typeof cleaned === 'object' && cleaned !== null && Object.keys(cleaned).length === 0)) {
                    return;
                }

                result[key] = cleaned;
            }
            else {
                result[key] = value;
            }
        });

        return result;
    }

    // Return primitives unchanged
    return obj;
};

export const mapBy = <T, K>(arr: T[], keyFn: (x: T) => K): Map<K, T[]> => {
    const map = new Map<K, T[]>();
    for (const x of arr) {
        const k = keyFn(x);
        if (!map.has(k)) {
            map.set(k, []);
        }
        map.get(k)!.push(x);
    }
    return map;
};
